/**
 * frontend/src/pages/valuation/_valuationDraft.ts
 *
 * VAL-INTAKE-1 D2/D3 — the shared draft hydrate/save hook used by both
 * ValuationTuning.tsx and ValuationIntake.tsx.
 *
 * Unlike `runCache` in `_shared.tsx`, this is NOT a
 * sessionStorage cache — the draft lives server-side, one row per
 * engagement (`apps/api/routers/_valuation_draft.py`), so two screens (and
 * two browser tabs) see the same draft. This hook only holds the load/save
 * state in React, on top of `fetchValuationDraft` / `saveValuationDraft`
 * (`api/valuationB3.ts`).
 *
 * D2 — "Saved · HH:MM": `save()` sets `savedAtLabel`; it renders nothing
 * itself, the caller does.
 * D3 — "Resuming your draft from …": exposed as `resuming` (true once a
 * non-empty draft has loaded) + `resumedAtLabel`, with `dismiss()` as the
 * "start clean" affordance. `dismiss()` only hides the banner locally — it
 * does NOT delete the server row (there is no delete route here, by design:
 * a screen that "starts clean" and then leaves without saving must not be
 * able to destroy the OTHER screen's half of the merge-on-write draft).
 *
 * VAL-FINALIZE-FROM-SAVED-1B — `updatedAt` is the server's stamp on the saved
 * draft, and a window `focus` refetches it, so a screen showing "what is saved"
 * (Sign-off) notices a save made in another tab. The refetch replaces `draft`
 * only when the stamp changed, and never flips `loading` (that is the initial
 * hydrate only).
 *
 * WIRE ASYMMETRY (see `types/valuationDraft.ts`): `draft` here is the GET
 * shape (`answers`); `save()`'s argument is the PUT shape (`driver_answers`).
 * They are typed as separate interfaces on purpose — do not conflate them.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchValuationDraft, saveValuationDraft } from '@/api/valuationB3'
import { formatShortDate, formatTimeCentral } from '@/utils/date'
import type { TuningBody } from '@/types/valuationB3'
import type { DraftExtras, DraftInputs, DraftTuning, SaveDraftBody } from '@/types/valuationDraft'

// WALKFIX-C F2: Central time; the server stamp is UTC, often with no zone.
function timeLabel(iso: string): string {
  return formatTimeCentral(iso)
}

function resumedLabel(iso: string): string {
  const date = formatShortDate(iso)
  if (!date) return ''
  return `${date}, ${timeLabel(iso)}`
}

function saveErrorMessage(err: unknown): string {
  const message = (err as { response?: { data?: { error?: { message?: unknown } } } })?.response
    ?.data?.error?.message
  return typeof message === 'string' && message ? message : 'Could not save your draft.'
}

/** Splits the saved `tuning` into the Methodology part (null when empty — the
 * Tuning screen reads every key in it as a knob) and the extras (typing keys,
 * P7b earnings choices). */
function splitTuning(t: DraftTuning | null | undefined): { tuning: TuningBody | null; extras: DraftExtras } {
  if (!t) return { tuning: null, extras: {} }
  const {
    signoff_reason, deferred_questions, earnings_override, earnings_basis, owner_figures_accepted, set_aside_reasons, value_methods,
    ...rest
  } = t
  const extras: DraftExtras = {}
  if (signoff_reason != null) extras.signoff_reason = signoff_reason
  if (deferred_questions != null) extras.deferred_questions = deferred_questions
  // VAL-PATH-SIGNER P7b — the signer's earnings choices, never preview knobs.
  if (earnings_override != null) extras.earnings_override = earnings_override
  if (earnings_basis != null) extras.earnings_basis = earnings_basis
  // VAL-PATH-TYPED-FINANCIALS P2c — the signer's acceptance, never a preview knob.
  if (owner_figures_accepted != null) extras.owner_figures_accepted = owner_figures_accepted
  // POL-B item 1 — the signer's set-aside reasons, never a preview knob.
  if (set_aside_reasons != null) extras.set_aside_reasons = set_aside_reasons
  // M1 / M-DEMO — cash, debt and the forecast rate, never a preview knob.
  if (value_methods != null) extras.value_methods = value_methods
  return { tuning: Object.keys(rest).length ? rest : null, extras }
}

export interface UseValuationDraft {
  /** null until the initial GET resolves, or if this engagement has no draft yet. */
  draft: DraftInputs | null
  /** VAL-INPUT-PERSIST-1E — the saved Tuning knobs + reason; null if none. */
  draftTuning: TuningBody | null
  /** VAL-SAVE-ON-TYPE-2 — the saved sign-off reason and "I'm not sure" answers. */
  draftExtras: DraftExtras
  /** True once a non-empty draft has loaded and the valuator has not dismissed it. */
  resuming: boolean
  /** e.g. "20 Aug, 4:12 PM" — only meaningful while `resuming` is true. */
  resumedAtLabel: string | null
  /** e.g. "Saved · 4:12 PM" — set after every successful save. */
  savedAtLabel: string | null
  /** Server stamp of the saved draft (`updated_at`); null if nothing is saved. */
  updatedAt: string | null
  /** True only during the initial hydrate GET. */
  loading: boolean
  /** Load or save failure — the caller decides how (or whether) to surface it. */
  error: string | null
  /** Merge-on-write save — pass only the fields this screen owns. Resolves to
   * `null` on success or the server's error message on failure (VAL-NAV-STATE-1
   * R3.6 — a save that cannot persist must raise a visible error; `error`
   * above is also set for a banner, but a caller acting on THIS save
   * specifically needs the outcome synchronously, not via a later render). */
  save: (body: SaveDraftBody) => Promise<string | null>
  /** "Start clean" — hides the resuming banner locally. Does not touch the server row. */
  dismiss: () => void
}

export function useValuationDraft(id: string | undefined): UseValuationDraft {
  const [draft, setDraft] = useState<DraftInputs | null>(null)
  const [draftTuning, setDraftTuning] = useState<TuningBody | null>(null)
  const [draftExtras, setDraftExtras] = useState<DraftExtras>({})
  const [resuming, setResuming] = useState(false)
  const [resumedAtLabel, setResumedAtLabel] = useState<string | null>(null)
  const [savedAtLabel, setSavedAtLabel] = useState<string | null>(null)
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const applyTuning = useCallback((t: DraftTuning | null | undefined) => {
    const split = splitTuning(t)
    setDraftTuning(split.tuning)
    setDraftExtras(split.extras)
  }, [])
  const saveSeq = useRef(0)
  const seenStamp = useRef<string | null>(null)

  useEffect(() => {
    if (!id) {
      setLoading(false)
      return
    }
    let live = true
    setLoading(true)
    fetchValuationDraft(id)
      .then(res => {
        if (!live) return
        setDraft(res.inputs)
        applyTuning(res.tuning)
        seenStamp.current = res.updated_at
        setUpdatedAt(res.updated_at)
        if (res.inputs && res.updated_at) {
          setResuming(true)
          setResumedAtLabel(resumedLabel(res.updated_at))
        }
      })
      .catch(() => {
        if (live) setError('Could not load your saved draft.')
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [id, applyTuning])

  // Another tab saved while this one sat in the background: pick it up on focus.
  useEffect(() => {
    if (!id) return
    let live = true
    const onFocus = () => {
      const seq = saveSeq.current
      fetchValuationDraft(id)
        .then(res => {
          if (!live || seq !== saveSeq.current) return
          if (res.updated_at === seenStamp.current) return
          seenStamp.current = res.updated_at
          setDraft(res.inputs)
          applyTuning(res.tuning)
          setUpdatedAt(res.updated_at)
        })
        .catch(() => { /* a failed background refresh keeps what is on screen */ })
    }
    window.addEventListener('focus', onFocus)
    return () => {
      live = false
      window.removeEventListener('focus', onFocus)
    }
  }, [id, applyTuning])

  const save = useCallback(
    async (body: SaveDraftBody): Promise<string | null> => {
      if (!id) return 'Could not save your draft.'
      const mine = ++saveSeq.current
      try {
        const res = await saveValuationDraft(id, body)
        if (mine !== saveSeq.current) return null // a newer save already won
        setSavedAtLabel(`Saved · ${timeLabel(res.updated_at)}`)
        seenStamp.current = res.updated_at
        setUpdatedAt(res.updated_at)
        setError(null)
        return null
      } catch (err) {
        if (mine !== saveSeq.current) return null
        const message = saveErrorMessage(err)
        setError(message)
        return message
      }
    },
    [id],
  )

  const dismiss = useCallback(() => setResuming(false), [])

  return { draft, draftTuning, draftExtras, resuming, resumedAtLabel, savedAtLabel, updatedAt, loading, error, save, dismiss }
}
